import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useProjectPermissions } from "../../../routes/roleOnly";
import { fetchDeviations } from "../../../api/quality/deviation";
import { fetchChecklists } from "../../../api/quality/checklist";
import { fetchWorkOrders } from "../../../api/work-order";
import { fetchProjectActivities } from "../../../api/project_activity";
import { formatHoursValue } from "./projectRailUi";

function asCount(payload) {
  const hasErrors = Array.isArray(payload?.errors) ? payload.errors.length > 0 : Boolean(payload?.errors);
  if (payload == null || hasErrors || payload.success === false) return null;
  if (typeof payload.totalCount === "number") return payload.totalCount;
  if (typeof payload.total_count === "number") return payload.total_count;
  if (typeof payload.count === "number") return payload.count;
  if (Array.isArray(payload.data)) return payload.data.length;
  if (Array.isArray(payload.deviations)) return payload.deviations.length;
  if (Array.isArray(payload.checklists)) return payload.checklists.length;
  if (Array.isArray(payload.work_orders)) return payload.work_orders.length;
  return null;
}

function unwrap(res) {
  return res?.data !== undefined && (res.status != null || res.headers) ? res.data : res;
}

function projectFilters(projectId, extra = []) {
  return [{ name: "project", value: projectId }, ...extra];
}

export function ProjectOverviewStats({ project }) {
  const { t } = useTranslation();
  const projectPermissions = useProjectPermissions(project);
  const [stats, setStats] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!project?.id) return undefined;
    let cancelled = false;

    const load = async () => {
      setStats(null);
      setLoadError(false);
      const id = project.id;
      try {
        const [
          deviationsTotal,
          deviationsOpen,
          deviationsCritical,
          workOrdersTotal,
          workOrdersOngoing,
          checklistsTotal,
          checklistsDone,
          activities,
        ] = await Promise.all([
          fetchDeviations({ filters: projectFilters(id), per_page: 1, page: 1 }),
          fetchDeviations({
            filters: projectFilters(id, [{ name: "status", value: JSON.stringify(["1", "2"]) }]),
            per_page: 1,
            page: 1,
          }),
          fetchDeviations({
            filters: projectFilters(id, [
              { name: "status", value: JSON.stringify(["1", "2"]) },
              { name: "priority", value: JSON.stringify(["4"]) },
            ]),
            per_page: 1,
            page: 1,
          }),
          fetchWorkOrders({ filters: projectFilters(id), per_page: 1, page: 1 }),
          fetchWorkOrders({
            filters: projectFilters(id, [{ name: "status", value: JSON.stringify(["ongoing"]) }]),
            per_page: 1,
            page: 1,
          }),
          fetchChecklists({ filters: projectFilters(id), per_page: 1, page: 1 }),
          fetchChecklists({
            filters: projectFilters(id, [
              { name: "status", value: JSON.stringify(["finished", "approved", "closed"]) },
            ]),
            per_page: 1,
            page: 1,
          }),
          fetchProjectActivities(id, { throwOnInvalidResponse: true }),
        ]);
        if (cancelled) return;

        const activityRows = Array.isArray(activities) ? activities : [];
        const counts = [deviationsTotal, deviationsOpen, deviationsCritical,
          workOrdersTotal, workOrdersOngoing, checklistsTotal, checklistsDone]
          .map((result) => asCount(unwrap(result)));
        if (counts.some((count) => count == null)) throw new Error("Invalid project stats response");
        const loggedHours = activityRows.reduce(
          (sum, row) => sum + (Number(row.logged_hours) || 0),
          0
        );
        const budgetHours = activityRows.reduce((sum, row) => {
          if (row.budget_hours == null || row.budget_hours === "") return sum;
          return sum + (Number(row.budget_hours) || 0);
        }, 0);

        setStats({
          deviationsTotal: counts[0],
          deviationsOpen: counts[1],
          deviationsCritical: counts[2],
          workOrdersTotal: counts[3],
          workOrdersOngoing: counts[4],
          checklistsTotal: counts[5],
          checklistsDone: counts[6],
          loggedHours,
          budgetHours,
        });
      } catch (_err) {
        if (!cancelled) {
          setStats(null);
          setLoadError(true);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [project?.id, retry]);

  if (!project?.id) return null;

  const ready = stats != null;
  const hoursValue = ready
    ? stats.budgetHours > 0
      ? `${formatHoursValue(stats.loggedHours)} / ${formatHoursValue(stats.budgetHours)}`
      : formatHoursValue(stats.loggedHours)
    : "—";
  const hoursPct =
    ready && stats.budgetHours > 0
      ? Math.round((stats.loggedHours / stats.budgetHours) * 100)
      : null;
  const checklistPct =
    ready && stats.checklistsTotal > 0
      ? Math.round((stats.checklistsDone / stats.checklistsTotal) * 100)
      : null;

  const items = [
    {
      key: "deviations",
      label: t("project.overview.stats.open_deviations"),
      value: ready ? `${stats.deviationsOpen}/${stats.deviationsTotal}` : "—",
      note: ready
        ? t("project.overview.stats.note.critical", { count: stats.deviationsCritical })
        : " ",
      tone: ready && stats.deviationsCritical > 0 ? "var(--pv-warn)" : "var(--pv-hint)",
    },
    {
      key: "work_orders",
      label: t("project.overview.stats.work_orders"),
      value: ready ? String(stats.workOrdersTotal) : "—",
      note: ready
        ? t("project.overview.stats.note.ongoing", { count: stats.workOrdersOngoing })
        : " ",
      tone: "var(--pv-hint)",
    },
    {
      key: "checklists",
      label: t("project.overview.stats.checklists"),
      value: ready ? `${stats.checklistsDone}/${stats.checklistsTotal}` : "—",
      note: ready
        ? checklistPct != null
          ? t("project.overview.stats.note.checklist_pct", { pct: checklistPct })
          : t("project.overview.stats.note.none")
        : " ",
      tone: "var(--pv-hint)",
    },
    {
      key: "hours",
      label: t("project.overview.stats.hours"),
      value: hoursValue,
      note: ready
        ? hoursPct != null
          ? t("project.overview.stats.note.budget_pct", { pct: hoursPct })
          : t("project.overview.stats.note.no_budget")
        : " ",
      tone: hoursPct != null && hoursPct >= 100 ? "var(--pv-warn)" : "var(--pv-hint)",
    },
  ];

  return (
    <div className="pv-stats">
      {loadError ? <div className="pv-stats-error" role="alert">
        <span>{t("project.overview.stats.load_error")}</span>
        <button type="button" onClick={() => setRetry((value) => value + 1)}>{t("Retry")}</button>
      </div> : null}
      {items.map((item) => (
        <div key={item.key} className="pv-stat">
          <span className="pv-stat-label">{item.label}</span>
          <span className="pv-stat-value">{item.value}</span>
          <span className="pv-stat-note" style={{ color: item.tone }}>
            {item.note}
          </span>
          {item.key === "hours" && projectPermissions.canTimeRegister ? (
            <Link className="pv-link" to={`/dashboard/projects/${project.id}?tab=timeoversikt`}>
              {t("project.overview.stats.view_time_overview")}
            </Link>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export default ProjectOverviewStats;
